import React, {useState, useEffect} from 'react';
import TextInput from 'components/inputs/TextInput';
import DateInput from 'components/inputs/DateInput';
import PhoneNumberInput from 'components/inputs/PhoneNumberInput';
import SSNInput from 'components/inputs/SSNInput';
import CurrencyInput from 'components/inputs/CurrencyInput';
import DateTimeInput from 'components/inputs/DateTimeInput';
import TimeInput from 'components/inputs/TimeInput';
import CheckboxInput from 'components/inputs/CheckboxInput';
import CustomSelect from 'components/inputs/CustomSelect';
import DescriptionInput from 'components/inputs/DescriptionInput';
import {DocumentTemplateElement} from 'types/documentTemplateElement';
import {User} from 'types/user';
import {JobRole} from 'types/jobRole';

interface DynamicFillOutOnCreationInputsProps {
  elements: DocumentTemplateElement[];
  onUpdateElement: (elementId: string, value: any) => void;
  user: User;
  filterSupervisorsByRole?: boolean;
  selectedJobRole?: JobRole;
  onUpdateAllInputsFilled: (allInputsFilled: boolean) => void;
}

const DynamicFillOutOnCreationInputs: React.FC<DynamicFillOutOnCreationInputsProps> = ({
  elements,
  onUpdateElement,
  user,
  onUpdateAllInputsFilled,
}) => {
  const [inputValues, setInputValues] = useState<Record<string, any>>({});
  const [dropdownOptions, setDropdownOptions] = useState<
    Record<string, Array<{value: string; label: string}>>
  >({});

  useEffect(() => {
    const initialValues: Record<string, any> = {};
    const updates: Array<{elementId: string; value: any}> = [];

    elements.forEach(element => {
      const inputId = element.id;
      let defaultValue: any = '';

      switch (element.elementType) {
        case 'TextInput':
        case 'MultiLineTextInput':
        case 'PhoneNumberInput':
        case 'SsnInput':
        case 'EmailInput':
        case 'AddressInput':
        case 'CurrencyInput':
        case 'DateTimeInput':
        case 'TimeInput':
        case 'UrlInput':
          defaultValue = element.properties.value || '';
          break;
        case 'YesNoInput':
        case 'CheckboxInput':
          defaultValue = element.properties.value || false;
          break;
        case 'DateInput':
          defaultValue = element.properties.value || '';
          break;
        case 'RatingInput':
          defaultValue = element.properties.value || element.properties.initialRating || '';
          break;
        case 'SignatureInput':
        case 'SignatureDateInput':
          defaultValue = element.properties.value || '';
          break;
        case 'Dropdown':
          defaultValue = element.properties.value || (element.properties.isMulti ? [] : '');
          break;
        default:
          defaultValue = '';
      }

      initialValues[inputId] = defaultValue;
      if (defaultValue !== '' && defaultValue !== false && defaultValue !== null) {
        updates.push({elementId: inputId, value: defaultValue});
      }
    });

    setInputValues(initialValues);

    updates.forEach(update => {
      onUpdateElement(update.elementId, update.value);
    });
  }, [elements, user]);

  useEffect(() => {
    const loadDropdownOptions = async () => {
      const options: Record<string, Array<{value: string; label: string}>> = {};

      for (const element of elements) {
        if (element.elementType === 'Dropdown') {
          const dropdownProps = element.properties as any;
          options[element.id] = dropdownProps.options?.map((option: string) => ({
            value: option,
            label: option,
          })) || [];
        }
      }
      setDropdownOptions(options);
    };
    loadDropdownOptions();
  }, [elements]);

  // Update validation logic
  useEffect(() => {
    const allInputsFilled =
      elements.length === 0 ||
      elements.every(element => {
        const value = inputValues[element.id];
        
        if (element.properties.required) {
          if (element.elementType === 'Dropdown' && element.properties.isMulti) {
            return Array.isArray(value) && value.length > 0;
          }
          return value !== '' && value !== false && value !== null && value !== undefined;
        }
        return true; // Not required, so it's considered filled
      });

    onUpdateAllInputsFilled(allInputsFilled);
  }, [elements, inputValues]);

  const handleChange = (elementId: string, value: any) => {
    setInputValues(prev => ({
      ...prev,
      [elementId]: value,
    }));
    onUpdateElement(elementId, value);
  };

  const renderElement = (element: DocumentTemplateElement) => {
    const elementProps = element.properties as any;
    const value = inputValues[element.id];

    switch (element.elementType) {
      case 'TextInput':
        return (
          <TextInput
            id={element.id}
            type="text"
            label={elementProps.showLabel ? elementProps.label : ''}
            value={value || ''}
            onChange={e => handleChange(element.id, e.target.value)}
            placeholder={elementProps.placeholder || ''}
            required={elementProps.required}
          />
        );

      case 'MultiLineTextInput':
        return (
          <DescriptionInput
            id={element.id}
            type="textarea"
            label={elementProps.showLabel ? elementProps.label : ''}
            value={value || ''}
            onChange={e => handleChange(element.id, e.target.value)}
            placeholder={elementProps.placeholder || ''}
            required={elementProps.required}
          />
        );

      case 'PhoneNumberInput':
        return (
          <PhoneNumberInput
            id={element.id}
            label={elementProps.showLabel ? elementProps.label : ''}
            value={value || ''}
            onChange={e => handleChange(element.id, e.target.value)}
            placeholder={elementProps.placeholder || ''}
            required={elementProps.required}
          />
        );

      case 'YesNoInput':
        return (
          <CheckboxInput
            id={element.id}
            label={elementProps.showLabel ? elementProps.label : ''}
            checked={value || false}
            onChange={e => handleChange(element.id, e.target.checked)}
            required={elementProps.required}
          />
        );

      case 'DateInput':
        return (
          <DateInput
            id={element.id}
            type="date"
            label={elementProps.showLabel ? elementProps.label : ''}
            value={value || ''}
            onChange={e => handleChange(element.id, e.target.value)}
            placeholder={elementProps.placeholder || 'MM/DD/YYYY'}
            required={elementProps.required}
          />
        );

      case 'SsnInput':
        return (
          <SSNInput
            id={element.id}
            label={elementProps.showLabel ? elementProps.label : ''}
            value={value || ''}
            onChange={e => handleChange(element.id, e.target.value)}
            required={elementProps.required}
          />
        );

      case 'EmailInput':
        return (
          <TextInput
            id={element.id}
            type="email"
            label={elementProps.showLabel ? elementProps.label : ''}
            value={value || ''}
            onChange={e => handleChange(element.id, e.target.value)}
            placeholder={elementProps.placeholder || ''}
            required={elementProps.required}
          />
        );

      case 'AddressInput':
        return (
          <DescriptionInput
            id={element.id}
            type="textarea"
            label={elementProps.showLabel ? elementProps.label : ''}
            value={value || ''}
            onChange={e => handleChange(element.id, e.target.value)}
            placeholder={elementProps.placeholder || ''}
            required={elementProps.required}
          />
        );

      case 'CurrencyInput':
        return (
          <CurrencyInput
            id={element.id}
            label={elementProps.showLabel ? elementProps.label : ''}
            value={typeof value === 'number' ? value : undefined}
            onChange={(val: number | undefined) => handleChange(element.id, val)}
            placeholder={elementProps.placeholder || ''}
            required={elementProps.required}
          />
        );

      case 'DateTimeInput':
        return (
          <DateTimeInput
            id={element.id}
            label={elementProps.showLabel ? elementProps.label : ''}
            value={value || ''}
            onChange={(val: string) => handleChange(element.id, val)}
            placeholder={elementProps.placeholder || ''}
            required={elementProps.required}
          />
        );

      case 'TimeInput':
        return (
          <TimeInput
            id={element.id}
            label={elementProps.showLabel ? elementProps.label : ''}
            value={value || ''}
            onChange={(val: string) => handleChange(element.id, val)}
            placeholder={elementProps.placeholder || ''}
            required={elementProps.required}
          />
        );

      case 'RatingInput':
        return (
          <div className="flex flex-col gap-[0.625rem] w-full">
            <label
              htmlFor={element.id}
              className="w-full block grow shrink basis-0 text-slate-800 text-sm font-semibold font-['Inter'] dark:text-white">
              {elementProps.showLabel ? elementProps.label : ''}
              {elementProps.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              id={element.id}
              type="number"
              min="0"
              max={elementProps.maxRating || 5}
              value={value || 0}
              onChange={e => handleChange(element.id, parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required={elementProps.required}
            />
          </div>
        );

      case 'SignatureInput':
        return (
          <div className="flex flex-col gap-[0.625rem] w-full">
            <label
              htmlFor={element.id}
              className="w-full block grow shrink basis-0 text-slate-800 text-sm font-semibold font-['Inter'] dark:text-white">
              {elementProps.showLabel ? elementProps.label : ''}
              {elementProps.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <textarea
              id={element.id}
              value={value || ''}
              onChange={e => handleChange(element.id, e.target.value)}
              className="w-full h-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Signature placeholder"
              required={elementProps.required}
            />
          </div>
        );

      case 'SignatureDateInput':
        return (
          <div className="space-y-4">
            <div className="flex flex-col gap-[0.625rem] w-full">
              <label
                htmlFor={`${element.id}_signature`}
                className="w-full block grow shrink basis-0 text-slate-800 text-sm font-semibold font-['Inter'] dark:text-white">
                {elementProps.showLabel ? elementProps.label : ''}
                {elementProps.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              <textarea
                id={`${element.id}_signature`}
                value={value || ''}
                onChange={e => handleChange(element.id, e.target.value)}
                className="w-full h-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Signature placeholder"
                required={elementProps.required}
              />
            </div>
            <DateInput
              id={`${element.id}_date`}
              type="date"
              label={elementProps.showLabelDate ? elementProps.dateLabel : ''}
              value={value || ''}
              onChange={e => handleChange(element.id, e.target.value)}
              placeholder="MM/DD/YYYY"
              required={elementProps.required}
            />
          </div>
        );

      case 'UrlInput':
        return (
          <TextInput
            id={element.id}
            type="url"
            label={elementProps.showLabel ? elementProps.label : ''}
            value={value || ''}
            onChange={e => handleChange(element.id, e.target.value)}
            placeholder={elementProps.placeholder || ''}
            required={elementProps.required}
          />
        );

      case 'CheckboxInput':
        return (
          <CheckboxInput
            id={element.id}
            label={elementProps.showLabel ? elementProps.label : ''}
            checked={value || false}
            onChange={e => handleChange(element.id, e.target.checked)}
            required={elementProps.required}
          />
        );

      case 'Dropdown':
        return (
          <div className="flex flex-col gap-[0.625rem] w-full">
            <label
              htmlFor={element.id}
              className="w-full block grow shrink basis-0 text-slate-800 text-sm font-semibold font-['Inter'] dark:text-white">
              {elementProps.showLabel ? elementProps.label : ''}
              {elementProps.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <CustomSelect
              key={element.id}
              value={value || (elementProps.isMulti ? [] : '')}
              onChange={selectedOption => {
                if (elementProps.isMulti) {
                  const selectedValues = Array.isArray(selectedOption) 
                    ? selectedOption.map(option => option.value)
                    : [];
                  handleChange(element.id, selectedValues);
                } else {
                  handleChange(element.id, selectedOption.value);
                }
              }}
              options={dropdownOptions[element.id] || []}
              isMulti={elementProps.isMulti}
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="w-full space-y-4">
      {elements.map(element => (
        <div key={element.id} className="space-y-2">
          {renderElement(element)}
        </div>
      ))}
    </div>
  );
};

export default DynamicFillOutOnCreationInputs;
